/**
 * Scrambles text or numbers by randomly shuffling their characters/digits.
 *
 * This function takes a text value (string or number) and conditionally scrambles it
 * by randomly rearranging its characters or digits. The scrambling only occurs when
 * `shouldScramble` is true and the input is a string or number.
 *
 * For strings: characters are shuffled randomly
 * For numbers: digits are shuffled randomly and converted back to a number
 * For other types: the original value is returned unchanged
 *
 * The function preserves the exact input type, ensuring type safety and proper
 * TypeScript inference.
 *
 * @template T - The type of the input value, constrained to string | number | undefined | boolean | null
 * @param textValue The value to potentially scramble. Can be a string, number,
 *                    undefined, boolean, or null. Only strings and numbers will be scrambled.
 * @param shouldScramble Boolean flag that determines whether scrambling should occur.
 *                         If false, the original value is returned unchanged.
 *
 * @returns The scrambled value if scrambling occurred, otherwise the original value.
 *          The return type exactly matches the input type T.
 *          - For strings: returns a new string with shuffled characters
 *          - For numbers: returns a new number with shuffled digits
 *          - For other types: returns the original value unchanged
 *
 * @example
 * ```typescript
 * // Scramble a string - returns string type
 * const scrambled = TryScrambleText("hello", true);     // Type: string
 *
 * // Scramble a number - returns number type
 * const scrambledNum = TryScrambleText(12345, true);    // Type: number
 *
 * // No scrambling - returns original type unchanged
 * const unchanged = TryScrambleText("hello", false);    // Type: string
 * const unchangedNum = TryScrambleText(12345, false);   // Type: number
 *
 * // Non-scrambleable types return unchanged with exact type preservation
 * const bool = TryScrambleText(true, true);             // Type: true (literal type)
 * const nullable = TryScrambleText(null, true);         // Type: null
 * const optional = TryScrambleText(undefined, true);    // Type: undefined
 * ```
 */
export function TryScrambleText<T extends string | number | undefined | boolean | null>(textValue: T, shouldScramble: boolean): T {
  if (shouldScramble && (typeof textValue === 'string' || typeof textValue === 'number')) {
    let valueArray: (string | number)[] = [];

    if (typeof textValue === 'string') {
      valueArray = textValue.split('');
    } else if (typeof textValue === 'number') {
      valueArray = textValue.toString().split('').map(digit => parseInt(digit));
    }

    let currentIndex = valueArray.length;

    // While there are remaining elements to shuffle
    while (currentIndex !== 0) {
      // Pick a remaining element
      const randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;

      // Swap it with the current element
      const temp = valueArray[currentIndex];
      valueArray[currentIndex] = valueArray[randomIndex];
      valueArray[randomIndex] = temp;
    }

    if (typeof textValue === 'number') {
      return parseInt(valueArray.join('')) as T;
    } else {
      return valueArray.join('') as T;
    }
  }

  return textValue;
}
